/** Materialul pardoselii, umbra peretelui, lumina de pe muchie.
 *
 *  De ce există. Până acum un nivel de arhitectură se desena cu O SINGURĂ
 *  umplere — `#f2efe9` peste tot — și cu o singură grosime de linie. Rezultatul
 *  se citea ca o schemă de compartimentare: corect, dar plat, și mai ales
 *  imposibil de parcurs cu ochiul, fiindcă nimic nu deosebea o baie de un
 *  living în afară de cuvântul scris peste ea. Planșele după care lucrăm
 *  (vila Cheshme, planurile randate de sus) fac exact invers: FUNCȚIUNEA SE
 *  VEDE ÎN PARDOSEALĂ. Dale mari și deschise în zona de zi, lemn în dormitor,
 *  gresie mică în băi, beton la tehnic. Aici e stratul ăla.
 *
 *  Ce NU e. Nu e o declarație despre finisaje: documentul nu spune cu ce se
 *  pardosește, iar aplicația n-are voie să inventeze o măsurătoare. E o
 *  convenție de REPREZENTARE, aleasă pe `kind` — la fel cum poché-ul negru nu
 *  susține că peretele e din beton negru. De asta materialele stau într-un
 *  tabel citibil, nu ascunse prin componente, iar necunoscutul are un implicit
 *  declarat în loc să cadă pe „nimic".
 *
 *  Desenul e în METRI, deci și pasul hașurilor: o scândură are 0,18 m, o dală
 *  0,60 m, o gresie de baie 0,20 m. Asta le face să se îndesească firesc când
 *  omul mărește, fără niciun prag.
 */

import type { ReactNode } from "react";
import type { PickAttrs } from "../inspect";

/** Id-urile sunt prefixate `arh-` și emise O SINGURĂ DATĂ, din `Defs`.
 *
 *  Pe pagină pot sta mai multe `<svg>` deodată (pânza mare și previzualizările
 *  din boardul de soluții), iar toate poartă același `<Defs />`. Id-uri
 *  duplicate într-un document HTML sunt legale ca desen — `url(#arh-lemn)` se
 *  leagă de primul găsit, care are exact același conținut — dar NU e legal să
 *  se cheme la fel două lucruri diferite, deci fiecare pattern de aici trebuie
 *  să rămână unic prin NUME, nu prin instanță. */
export const MATERIALE = {
  dale: "arh-dale",
  lemn: "arh-lemn",
  gresie: "arh-gresie",
  beton: "arh-beton",
  punte: "arh-punte",
  apa: "arh-apa",
  planseu: "arh-planseu",
} as const;

export type Material = typeof MATERIALE[keyof typeof MATERIALE] | null;

/** Fondul de sub hașură. Hașura desenează doar rosturile; culoarea plină stă
 *  dedesubt, ca pattern-ul să nu care și fondul (un pattern cu fond propriu se
 *  vede „cusut" la marginea formei, unde tile-ul e tăiat). */
export const FOND: Record<string, string> = {
  "arh-dale": "#e9e1d5",
  "arh-lemn": "#d9c3a4",
  "arh-gresie": "#e3ded5",
  "arh-beton": "#d4cdc2",
  "arh-punte": "#c9b499",
  "arh-apa": "#cfdce2",
  "arh-planseu": "#d3dcdc",
};

/** Pardoseala fiecărei funcțiuni.
 *
 *  Cheile sunt `kind`-urile pe care le scriu ȘI generatorul, ȘI importul —
 *  măsurate pe documentul real al lui `testiana`: `living`, `bedroom`,
 *  `kitchen`, `bath`, `wc`, `hall`, `store`, `balcony`, `scara`, `lift`,
 *  `sas`, `bazin`. Restul intră pe implicit, dinadins: un `kind` necunoscut e
 *  o cameră despre care nu știm nimic, iar dalele neutre sunt exact ce se
 *  desenează când nu știi.
 *
 *  Perechile care par ciudate au motiv:
 *  - `hall` ia dale ca livingul, nu beton: holul unui apartament e spațiu
 *    locuit, iar în planșele de referință zona de zi curge fără prag prin el.
 *  - `balcony` ia PUNTE (scânduri late), nu lemn de interior: e singurul loc
 *    unde direcția scândurii spune „afară".
 *  - `scara` și `lift` iau beton fiindcă nucleul NU e locuință, iar planșa îl
 *    arată ca pe o piesă tehnică — ceea ce și e.
 */
// EXPORTAT ca proba să poată ține cele două tabele egale (v.
// `design/debara.test`). Nu e o poartă pentru cod nou: cine desenează cheamă
// `pardoseala(fel)`, nu tabelul.
export const PARDOSEALA: Record<string, Material> = {
  living: MATERIALE.dale,
  hall: MATERIALE.dale,
  hol: MATERIALE.dale,
  sas: MATERIALE.dale,
  bedroom: MATERIALE.lemn,
  dormitor: MATERIALE.lemn,
  kitchen: MATERIALE.gresie,
  bath: MATERIALE.gresie,
  bath2: MATERIALE.gresie,
  wc: MATERIALE.gresie,
  shower: MATERIALE.gresie,
  // DEBARAUA CALCĂ PE CE CALCĂ LIVINGUL — v. nota din `design/finisaje
  // ::PARDOSEALA_IMPLICITA`. Era beton, al spațiilor tehnice.
  //
  // «CE CALCĂ LIVINGUL» NU E ACELAȘI LUCRU ÎN CELE DOUĂ TABELE, și e
  // dinadins: în editor livingul are `parchet_standard`, iar aici are DALE,
  // fiindcă planul de nivel desenează materialul, nu sortimentul (v. nota de
  // sus — holul locuinței ia tot dale, ca zona de zi să curgă fără prag).
  // Debaraua îl urmează pe fiecare acolo unde e, nu copiază o cheie dintr-un
  // tabel în celălalt.
  store: MATERIALE.dale,
  ghena: MATERIALE.beton,
  scara: MATERIALE.beton,
  lift: MATERIALE.beton,
  tech: MATERIALE.beton,
  // zona de desfumare e o bucată din nucleu, deci beton ca el — v.
  // `kinds::desfumare`
  desfumare: MATERIALE.beton,
  balcony: MATERIALE.punte,
  bazin: MATERIALE.apa,
  // adăposturile ALA: grupul sanitar e o baie, SAS-ul și ieșirile sunt beton;
  // încăperea de adăpostit însăși n-are pardoseală desenată (e transparentă,
  // v. `StoreyView`), ca parcarea și stâlpii de dedesubt să rămână citeți
  ala_gs: MATERIALE.gresie,
  ala_sas: MATERIALE.beton,
  ala_iesire: MATERIALE.beton,
};

/** Materialul unei camere. Implicitul e declarat, nu tăcut. */
/** FELURILE DE ZONĂ: camere care se citesc ca o ÎNSEMNARE pe planșă, nu ca o
 *  încăpere — și de-aia își poartă conturul roșu.
 *
 *  ROȘUL E AL ARHITECTULUI, nu o culoare inventată aici: pe planșele ei zonele
 *  de desfumare sunt trase cu roșu, și tot de pe el le citește importul
 *  (v. promptul din `importer/plan/agent.py`). Regula alb-negru a planșei
 *  rămâne, fiindcă ea e despre SUPRAFEȚE: pardoseala zonei e tot betonul
 *  nucleului, se colorează numai linia.
 *
 *  STĂ AICI, NU ÎN DESENUL TRONSONULUI, și asta a costat două livrări: pusă
 *  întâi numai în `tronson/Desen`, zona ieșea roșie în editor și neagră pe
 *  planul de nivel — adică exact pe ecranul pe care se uita arhitectul. Un
 *  contur de cameră se trage din două locuri, iar regula trebuie să fie una. */
export const FELURI_ZONA = new Set(["desfumare"]);
export const ZONA_CONTUR = "#cc2b20";
/** În pixeli: mai apăsat decât orice linie de cameră, altfel nu se vede
 *  (v. `tronson/Desen`, unde a fost 0,75 și a trecut neobservată). */
export const ZONA_GROSIME = 2.25;

/** Conturul unei camere, când ea e o zonă. `null` pentru orice altceva. */
export function conturZonei(kind?: string | null):
  { stroke: string; strokeWidth: number } | null {
  return kind && FELURI_ZONA.has(kind)
    ? { stroke: ZONA_CONTUR, strokeWidth: ZONA_GROSIME } : null;
}

export function materialul(kind?: string | null): Material {
  if (!kind) return MATERIALE.dale;
  return PARDOSEALA[kind] ?? MATERIALE.dale;
}

/** Sub cât nu se mai desenează hașura, în pixeli pe metru.
 *
 *  Rostul unei dale e la 0,60 m; sub 9 px/m ăsta cade sub 5,4 px, adică sub
 *  distanța la care două linii se mai văd ca două linii. Peste prag hașura
 *  informează; sub el e o ceață gri care întunecă planul fără să spună nimic —
 *  și exact asta se întâmpla la încadrarea pe tot ansamblul, unde sunt șase
 *  corpuri pe ecran. Fondul plin rămâne desenat oricum, deci funcțiunea tot se
 *  deosebește după culoare. */
export const PRAG_HASURA = 9;

const R_ROST = "#d7cdbd";      // rostul dalei — cald, nu gri
const R_LEMN = "#c2a984";      // rostul dintre scânduri
const R_GRESIE = "#d3ccc0";
const R_BETON = "#c4bcae";
const R_APA = "#b3c8d3";

/** Pattern-urile și filtrele, emise o singură dată din `Defs`. */
export function ArhDefs() {
  return (
    <>
      {/* Dale mari, 0,60 m: două linii pe tile, deci un caroiaj adevărat, nu
          dungi. `shapeRendering="crispEdges"` NU se pune — la rotirea planului
          ar face rosturile să salte de la un pixel la altul. */}
      <pattern id={MATERIALE.dale} width={0.6} height={0.6} patternUnits="userSpaceOnUse">
        <line x1={0} y1={0} x2={0.6} y2={0} stroke={R_ROST} strokeWidth={0.012} />
        <line x1={0} y1={0} x2={0} y2={0.6} stroke={R_ROST} strokeWidth={0.012} />
      </pattern>

      {/* Scândură de 0,18 m, cu rosturi de cap decalate: fără decalaj, capetele
          se aliniază într-o a doua linie continuă și covorul de parchet arată
          ca un caroiaj, adică exact ca dalele de deasupra. */}
      <pattern id={MATERIALE.lemn} width={1.8} height={0.18} patternUnits="userSpaceOnUse">
        <line x1={0} y1={0} x2={1.8} y2={0} stroke={R_LEMN} strokeWidth={0.01} />
        <line x1={0} y1={0} x2={0} y2={0.18} stroke={R_LEMN} strokeWidth={0.008} />
        <line x1={0.9} y1={0.09} x2={0.9} y2={0.18} stroke={R_LEMN} strokeWidth={0.008} />
      </pattern>

      {/* Gresie de 0,20 m — pasul mic e chiar semnul unei încăperi umede. */}
      <pattern id={MATERIALE.gresie} width={0.2} height={0.2} patternUnits="userSpaceOnUse">
        <line x1={0} y1={0} x2={0.2} y2={0} stroke={R_GRESIE} strokeWidth={0.008} />
        <line x1={0} y1={0} x2={0} y2={0.2} stroke={R_GRESIE} strokeWidth={0.008} />
      </pattern>

      {/* Betonul n-are rost, are PESTRIȚĂTURĂ: patru puncte pe metru, așezate
          neregulat în tile ca să nu se citească un caroiaj. */}
      <pattern id={MATERIALE.beton} width={1} height={1} patternUnits="userSpaceOnUse">
        <circle cx={0.17} cy={0.31} r={0.022} fill={R_BETON} />
        <circle cx={0.63} cy={0.14} r={0.018} fill={R_BETON} />
        <circle cx={0.41} cy={0.72} r={0.02} fill={R_BETON} />
        <circle cx={0.86} cy={0.58} r={0.016} fill={R_BETON} />
      </pattern>

      {/* Puntea de balcon: scânduri late, DOAR pe o direcție. */}
      <pattern id={MATERIALE.punte} width={0.14} height={0.14} patternUnits="userSpaceOnUse">
        <line x1={0} y1={0} x2={0} y2={0.14} stroke={R_LEMN} strokeWidth={0.016} />
      </pattern>

      {/* Planșeul FĂRĂ program: partea de placă pe care solverul n-a așezat
          apartamente. Nu e o greșeală de desen și nu e o cameră — e suprafață
          construită neocupată, și trebuie să se citească exact așa. Desenată ca
          până acum (alb, cu contur gros de tuș) arăta ca o încăpere uriașă
          goală, adică exact ca un bug; hașura rară o trimite în fundal fără s-o
          ascundă. */}
      <pattern id={MATERIALE.planseu} width={1.4} height={1.4}
               patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1={0} y1={0} x2={0} y2={1.4} stroke="#cdc4b4" strokeWidth={0.03} />
      </pattern>

      <pattern id={MATERIALE.apa} width={0.5} height={0.5} patternUnits="userSpaceOnUse">
        <path d="M0 0.25 Q0.125 0.17 0.25 0.25 T0.5 0.25" fill="none"
              stroke={R_APA} strokeWidth={0.016} />
      </pattern>

      {/* Umbra peretelui pe pardoseală. Lumina vine din stânga-sus, ca în toate
          planșele de referință, deci umbra cade dreapta-jos.
          Un SINGUR filtru, pus pe GRUPUL de pereți — nu pe fiecare perete. Pe
          un etaj cu 31 de compartimentări, 31 de filtre înseamnă 31 de pânze
          intermediare de rasterizat. */}
      <filter id="arh-umbra" x="-5%" y="-5%" width="115%" height="115%">
        <feDropShadow dx={0.07} dy={0.09} stdDeviation={0.05}
                      floodColor="#4a4239" floodOpacity={0.34} />
      </filter>

      {/* Umbra peretelui ALB din vederea randată. Acolo peretele nu mai e o
          pată de tuș, ci un volum deschis — exact ca în planșele de referință —
          deci tot ce îl arată e umbra pe care o lasă pe pardoseală. De aceea e
          mai lungă, mai difuză și mai apăsată decât `arh-umbra`: ea DESENEAZĂ
          peretele, nu îl accentuează. */}
      <filter id="arh-umbra-alb" x="-10%" y="-10%" width="128%" height="128%">
        {/* DOUĂ umbre, nu una: una scurtă și închisă, care lipește peretele de
            pardoseală, și una lungă și difuză, care îi dă înălțime. Cu una
            singură iese ori o dungă tăioasă (pare decupaj), ori o ceață (pare
            murdărie) — planșele de referință au amândouă, și de asta pereții
            lor arată ca volume, nu ca benzi. */}
        <feDropShadow dx={0.05} dy={0.07} stdDeviation={0.04}
                      floodColor="#5c4c38" floodOpacity={0.5} />
        <feDropShadow dx={0.2} dy={0.28} stdDeviation={0.2}
                      floodColor="#6b5c48" floodOpacity={0.4} />
      </filter>

      {/* Fondul de hârtie pe care stă planșa. În ambele referințe desenul NU
          plutește pe alb: stă pe un carton cald, ușor pătat, iar contrastul cu
          el e ce face planul să pară un obiect așezat, nu o fereastră. */}
      {/* HAȘURILE PLANȘEI TEHNICE — v. `HASURA_ROL` din `StoreyView`.
      
          STAS 1434-83 (tabelul 2) dă simbolurile de material în secțiune, iar
          STAS 104-80 / SR ISO 128-50 mecanica hașurii: linie continuă SUBȚIRE
          (clasa C3 ≈ b/4), la 45° față de chenar, cu pasul păstrat pe toată
          planșa, și cu sensul INVERSAT pe câmpuri vecine ca să se citească
          separat.
      
          Fără fond propriu: un `<rect>` alb în tile s-ar vedea ca o cusătură la
          marginea formei. Poligonul de dedesubt dă albul; ăsta doar dungile. */}
      <pattern id="arh-hartie" width={7} height={7} patternUnits="userSpaceOnUse">
        <rect width={7} height={7} fill="#cabca9" />
        <circle cx={1.4} cy={2.1} r={1.9} fill="#c5b6a2" opacity={0.55} />
        <circle cx={5.2} cy={4.8} r={2.4} fill="#cfc2b0" opacity={0.5} />
        <circle cx={3.4} cy={6.2} r={1.4} fill="#c2b39e" opacity={0.45} />
      </pattern>

      {/* Umbra mobilierului: mai scurtă și mai moale — piesele sunt joase. */}
      <filter id="arh-umbra-mobila" x="-10%" y="-10%" width="125%" height="125%">
        <feDropShadow dx={0.035} dy={0.045} stdDeviation={0.03}
                      floodColor="#4a4239" floodOpacity={0.26} />
      </filter>
    </>
  );
}

/** Pardoseala unei camere: fondul plin, apoi rostul.
 *
 *  Două forme, nu una cu `fill="url(...)"` peste o culoare: un pattern nu are
 *  fond propriu (v. `FOND`), iar dacă i-l dai, marginea tăiată a tile-ului se
 *  vede ca o dungă la conturul camerei.
 *
 *  `pick` stă pe fondul plin, care e forma de dedesubt și cea care primește
 *  click-ul; stratul de rost e `pointerEvents="none"`, altfel ar fura apăsarea
 *  fără să fie el obiectul.
 */
export function Pardoseala({ points, kind, hasura, stroke, strokeWidth, pick,
                            fond: fondCerut, children }: {
  /** Inelul, în forma pe care o scrie `StoreyView.puncte()`. */
  points: string;
  kind?: string | null;
  /** Rosturile se desenează? Vezi `PRAG_HASURA`. */
  hasura: boolean;
  stroke: string;
  strokeWidth: number;
  /** Fondul cerut de convenție, când desenul nu vrea tonul de pardoseală.
   *
   *  Planșa tehnică n-are pardoseli: camera e HÂRTIE, iar singurul ton de pe
   *  foaie e grosimea zidului. Fără asta, o cameră fără material cădea pe
   *  „#f3efe8" — un bej cald, corect pe planul randat și fals pe unul
   *  alb-negru, unde făcea toată planșa să pară colorată pe jumătate. */
  fond?: string;
  pick?: PickAttrs;
  children?: ReactNode;
}) {
  const mat = materialul(kind);
  const fond = fondCerut ?? (mat && FOND[mat]) ?? "#f3efe8";
  return (
    <>
      <polygon points={points} fill={fond} stroke={stroke}
               strokeWidth={strokeWidth} vectorEffect="non-scaling-stroke"
               {...pick} />
      {hasura && mat && (
        <polygon points={points} fill={`url(#${mat})`} pointerEvents="none" />
      )}
      {children}
    </>
  );
}
